import { t as uiText } from '../../i18n';
import { db } from '../../lib/firebase';
import {
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
  deleteField,
} from 'firebase/firestore';

const DEFAULT_SERIES_ID = 'NCN';
const STANDALONE_PLAN_ID = 'free';

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function resolveSeriesId(data: any): string {
  return (
    normalizeString(data?.seriesId) ||
    normalizeString(data?.productId) ||
    normalizeString(data?.series) ||
    DEFAULT_SERIES_ID
  );
}

function toOfferId(seriesId: string, planId: string): string {
  return `${String(seriesId).toLowerCase()}:${String(planId).toLowerCase()}`;
}

export type WorkspaceLinkType = 'none' | 'company' | 'workspace';

export interface UpdateWorkspaceLinkageParams {
  workspaceId: string;
  linkType: WorkspaceLinkType;
  companyId?: string; // linkType==='company' のとき必須
  parentWorkspaceId?: string; // linkType==='workspace' のとき必須
}

/**
 * ワークスペースの紐付け（companyId / parentWorkspaceId）を変更する。
 * - workspaces/{workspaceId} の companyId / parentWorkspaceId を更新
 * - companies/{companyId}/workspaces/{workspaceId} を更新
 * - workspaces/{parentWorkspaceId}/childWorkspaces/{workspaceId} を更新
 * - linkType==='none' のときは企業・親の両方を外す
 * 親に companyId が無い場合は子の parentWorkspaceId のみ設定し、子の companyId は親からは埋めない。
 */
export async function updateWorkspaceLinkage(params: UpdateWorkspaceLinkageParams): Promise<void> {
  const { workspaceId, linkType } = params;

  if (!workspaceId) {
    throw new Error('workspaceId is required');
  }

  if (linkType === 'company' && !params.companyId) {
    throw new Error('companyId is required when linkType is company');
  }

  if (linkType === 'workspace' && !params.parentWorkspaceId) {
    throw new Error('parentWorkspaceId is required when linkType is workspace');
  }

  const workspaceRef = doc(db, 'workspaces', workspaceId);
  const workspaceSnap = await getDoc(workspaceRef);

  if (!workspaceSnap.exists()) {
    throw new Error(uiText("Workspace not found"));
  }

  const current = workspaceSnap.data() as any;
  const oldCompanyId: string | undefined = current?.companyId;
  const oldParentWorkspaceId: string | undefined = current?.parentWorkspaceId;

  let newCompanyId: string | undefined = undefined;
  let newParentWorkspaceId: string | undefined = undefined;
  let nextPlanId: string | undefined = undefined;
  let nextSeriesId: string | undefined = undefined;
  let nextOfferId: string | undefined = undefined;

  if (linkType === 'none') {
    newCompanyId = undefined;
    newParentWorkspaceId = undefined;
  } else if (linkType === 'company') {
    newCompanyId = params.companyId!;
    newParentWorkspaceId = undefined;
  } else {
    newParentWorkspaceId = params.parentWorkspaceId!;
    // 親ワークスペースから companyId を継承
    const parentRef = doc(db, 'workspaces', newParentWorkspaceId);
    const parentSnap = await getDoc(parentRef);
    if (!parentSnap.exists()) {
      throw new Error(uiText("Parent workspace not found"));
    }
    const parentData = parentSnap.data() as any;
    // 親に企業が未設定でも親子関係は保存する（companyId は未定義のまま deleteField でクリア可能）
    newCompanyId = parentData?.companyId || undefined;
    nextSeriesId = resolveSeriesId(parentData);
    const parentPlan = normalizeString(parentData?.planId);
    nextPlanId = parentPlan && parentPlan !== 'child' ? parentPlan : STANDALONE_PLAN_ID;
    const parentOffer = normalizeString(parentData?.offerId);
    nextOfferId = parentOffer && !/:child$/i.test(parentOffer)
      ? parentOffer
      : toOfferId(nextSeriesId, nextPlanId);
  }

  // 親から外したときは独立の free に戻す
  if (!newParentWorkspaceId && oldParentWorkspaceId) {
    nextSeriesId = resolveSeriesId(current);
    nextPlanId = STANDALONE_PLAN_ID;
    nextOfferId = toOfferId(nextSeriesId, nextPlanId);
  }

  // 変更が無ければ何もしない
  const companyUnchanged = (oldCompanyId || '') === (newCompanyId || '');
  const parentUnchanged = (oldParentWorkspaceId || '') === (newParentWorkspaceId || '');
  if (companyUnchanged && parentUnchanged) {
    return;
  }

  const batch = writeBatch(db);

  // workspaces/{workspaceId}
  const workspaceUpdatePayload: Record<string, unknown> = {
    companyId: newCompanyId ?? deleteField(),
    parentWorkspaceId: newParentWorkspaceId ?? deleteField(),
    updatedAt: serverTimestamp(),
  };
  if (nextPlanId) {
    workspaceUpdatePayload.planId = nextPlanId;
    workspaceUpdatePayload.seriesId = nextSeriesId;
    workspaceUpdatePayload.offerId = nextOfferId;
  }
  batch.update(workspaceRef, workspaceUpdatePayload);

  // companies/{companyId}/workspaces/{workspaceId}
  if (oldCompanyId && oldCompanyId !== newCompanyId) {
    const oldCompanyWorkspaceRef = doc(db, 'companies', oldCompanyId, 'workspaces', workspaceId);
    batch.delete(oldCompanyWorkspaceRef);
  }
  if (newCompanyId && oldCompanyId !== newCompanyId) {
    const newCompanyWorkspaceRef = doc(db, 'companies', newCompanyId, 'workspaces', workspaceId);
    batch.set(newCompanyWorkspaceRef, {
      workspaceId,
      createdAt: serverTimestamp(),
    });
  }

  // 親ワークスペース childWorkspaces
  if (oldParentWorkspaceId && oldParentWorkspaceId !== newParentWorkspaceId) {
    const oldParentChildRef = doc(db, 'workspaces', oldParentWorkspaceId, 'childWorkspaces', workspaceId);
    batch.delete(oldParentChildRef);
  }
  if (newParentWorkspaceId && oldParentWorkspaceId !== newParentWorkspaceId) {
    const newParentChildRef = doc(db, 'workspaces', newParentWorkspaceId, 'childWorkspaces', workspaceId);
    batch.set(newParentChildRef, {
      workspaceId,
      createdAt: serverTimestamp(),
    });
  }

  try {
    await batch.commit();
  } catch (error) {
    console.error('🔄 updateWorkspaceLinkage: エラーが発生しました:', error);
    throw new Error(uiText("Couldn't update the link"));
  }
}

